import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, TrendingUp, DollarSign, ShoppingCart, Users, 
  Music, Video, Calendar, Download, RefreshCw, BarChart3,
  PieChart, Activity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [revenueByCategory, setRevenueByCategory] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [salesOverTime, setSalesOverTime] = useState([]);
  const [classStats, setClassStats] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_auth');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Basic ${localStorage.getItem('admin_token') || sessionStorage.getItem('admin_auth')}` }
  });

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, summaryRes, categoryRes, topRes, salesRes, classRes] = await Promise.all([
        axios.get(`${API}/api/admin/analytics/overview`, getAuthHeaders()),
        axios.get(`${API}/api/admin/analytics/summary`, getAuthHeaders()),
        axios.get(`${API}/api/admin/analytics/revenue-by-category`, getAuthHeaders()),
        axios.get(`${API}/api/admin/analytics/top-products?limit=5`, getAuthHeaders()),
        axios.get(`${API}/api/admin/analytics/sales-over-time?days=14`, getAuthHeaders()),
        axios.get(`${API}/api/admin/analytics/class-registrations`, getAuthHeaders())
      ]);
      
      setOverview(overviewRes.data);
      setSummary(summaryRes.data);
      setRevenueByCategory(categoryRes.data);
      setTopProducts(topRes.data);
      setSalesOverTime(salesRes.data);
      setClassStats(classRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const maxRevenue = Math.max(...salesOverTime.map(d => d.revenue), 1);

  return (
    <div className="min-h-screen bg-stone-950 text-white">
      {/* Header */}
      <div className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-amber-500" />
                  Analytics Dashboard
                </h1>
                <p className="text-sm text-stone-400">Comprehensive business insights</p>
              </div>
            </div>
            <button
              onClick={fetchAllData}
              className="flex items-center gap-2 px-4 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Quick Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-400 text-sm font-medium">Today's Revenue</p>
                <p className="text-3xl font-bold text-white">${summary?.today?.revenue || 0}</p>
                <p className="text-sm text-stone-400">{summary?.today?.sales || 0} sales</p>
              </div>
              <DollarSign className="w-10 h-10 text-green-500/50" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-400 text-sm font-medium">This Week</p>
                <p className="text-3xl font-bold text-white">${summary?.this_week?.revenue || 0}</p>
                <p className="text-sm text-stone-400">{summary?.this_week?.sales || 0} sales</p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-500/50" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-400 text-sm font-medium">All Time Revenue</p>
                <p className="text-3xl font-bold text-white">${summary?.all_time?.revenue || 0}</p>
                <p className="text-sm text-stone-400">{summary?.all_time?.sales || 0} total sales</p>
              </div>
              <ShoppingCart className="w-10 h-10 text-purple-500/50" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-xl p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-400 text-sm font-medium">Pending Actions</p>
                <p className="text-3xl font-bold text-white">{summary?.pending_actions?.total || 0}</p>
                <p className="text-sm text-stone-400">Need attention</p>
              </div>
              <Activity className="w-10 h-10 text-amber-500/50" />
            </div>
          </motion.div>
        </div>

        {/* Sales Chart & Revenue by Category */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sales Over Time */}
          <div className="lg:col-span-2 bg-stone-900 border border-stone-800 rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-500" />
              Sales (Last 14 Days)
            </h3>
            <div className="h-48 flex items-end gap-1">
              {salesOverTime.map((day, index) => (
                <div key={day.date} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full bg-amber-500/80 rounded-t transition-all hover:bg-amber-400"
                    style={{ height: `${(day.revenue / maxRevenue) * 100}%`, minHeight: day.revenue > 0 ? '4px' : '0' }}
                    title={`${day.date}: $${day.revenue}`}
                  />
                  {index % 2 === 0 && (
                    <span className="text-xs text-stone-500 mt-1 rotate-45 origin-left">
                      {day.date.slice(5)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Revenue by Category */}
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-amber-500" />
              Revenue by Category
            </h3>
            <div className="space-y-4">
              {revenueByCategory.map((cat, index) => {
                const colors = ['bg-amber-500', 'bg-blue-500', 'bg-purple-500'];
                const total = revenueByCategory.reduce((sum, c) => sum + c.revenue, 0) || 1;
                const percent = Math.round((cat.revenue / total) * 100);
                
                return (
                  <div key={cat.type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{cat.category}</span>
                      <span className="text-stone-400">${cat.revenue} ({percent}%)</span>
                    </div>
                    <div className="h-2 bg-stone-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${colors[index]} transition-all`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Inventory & Top Products */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Inventory Summary */}
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4">Inventory & Engagement</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-stone-800 rounded-lg p-4 text-center">
                <Music className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{overview?.inventory?.tracks || 0}</p>
                <p className="text-sm text-stone-400">Tracks</p>
              </div>
              <div className="bg-stone-800 rounded-lg p-4 text-center">
                <Video className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{overview?.inventory?.video_courses || 0}</p>
                <p className="text-sm text-stone-400">Video Courses</p>
              </div>
              <div className="bg-stone-800 rounded-lg p-4 text-center">
                <Calendar className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{overview?.inventory?.classes || 0}</p>
                <p className="text-sm text-stone-400">Classes</p>
              </div>
              <div className="bg-stone-800 rounded-lg p-4 text-center">
                <Users className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{overview?.engagement?.mailing_list_subscribers || 0}</p>
                <p className="text-sm text-stone-400">Subscribers</p>
              </div>
            </div>
          </div>

          {/* Top Products */}
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4">Top Selling Products</h3>
            {topProducts.length === 0 ? (
              <p className="text-stone-500 text-center py-8">No sales yet</p>
            ) : (
              <div className="space-y-3">
                {topProducts.map((product, index) => (
                  <div key={product.id} className="flex items-center gap-3 p-2 bg-stone-800 rounded-lg">
                    <span className="text-lg font-bold text-amber-500 w-6">{index + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{product.title}</p>
                      <p className="text-sm text-stone-400 capitalize">{product.type?.replace('_', ' ')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-400">${product.revenue}</p>
                      <p className="text-xs text-stone-500">{product.sales_count} sales</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Class Registration Stats */}
        {classStats.length > 0 && (
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" />
              Class Registration Stats
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-stone-400 border-b border-stone-800">
                    <th className="pb-3">Class</th>
                    <th className="pb-3">Schedule</th>
                    <th className="pb-3 text-center">Total</th>
                    <th className="pb-3 text-center">Active</th>
                    <th className="pb-3 text-center">Per Class</th>
                    <th className="pb-3 text-center">Monthly</th>
                    <th className="pb-3 text-center">Yearly</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-800">
                  {classStats.map(cls => (
                    <tr key={cls.class_id}>
                      <td className="py-3 font-medium">{cls.class_name}</td>
                      <td className="py-3 text-sm text-stone-400">{cls.day} {cls.time}</td>
                      <td className="py-3 text-center font-bold">{cls.total_registrations}</td>
                      <td className="py-3 text-center text-green-400">{cls.active}</td>
                      <td className="py-3 text-center">{cls.membership_breakdown?.per_class || 0}</td>
                      <td className="py-3 text-center">{cls.membership_breakdown?.monthly || 0}</td>
                      <td className="py-3 text-center">{cls.membership_breakdown?.yearly || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
